import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
  Link
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import EditableItemList from './common/EditableItemList';
import RichTextEditor from '../../../common/RichTextEditor';
import { toPickerValue, fromPickerValue } from '../../../../utils/dateOnly';
import { useCV } from '../contexts/CVContext';
import { formatDateRange } from '../helpers/ProcessTemplate';
import Modal from '../../../common/Modal';
import RolesSelection from '../../../ProjectsManagement/ProjectForm/components/Employees/components/EmployeeTransfer/components/RolesSelection';
import { ROLE_TYPES } from '../../../../constants/roleTypes';
//import { DEPARTMENT_TYPES } from '../../../../constants/radar';
import { ORG_UNIT_TYPES } from '../../../../constants/radar';
import SkillsStandalone from './SkillsStandalone';
import ProjectSearch from '../../../common/ProjectSearch';
import WorkIcon from '@mui/icons-material/Work';
import CloseIcon from '@mui/icons-material/Close';
import { ORGANIZATION_NAME } from '../../../../constants/radar';
import PositionGradeRolesBox from './common/PositionGradeRolesBox';
import { PROJECT_PURPOSE_TYPES } from '../../../../constants/projectPurposes';

const EmploymentHistory = () => {
  const { isDataReady, cvData, updateEmploymentHistory, readonly } = useCV();
  
  // State for roles modal
  const [showRolesModal, setShowRolesModal] = useState(false);
  const [selectedPositionForRoles, setSelectedPositionForRoles] = useState(null);
  const [selectedFunctionalRoles, setSelectedFunctionalRoles] = useState([]);
  const [selectedTechnicalRoles, setSelectedTechnicalRoles] = useState([]);
  const [selectedOrganizationalRoles, setSelectedOrganizationalRoles] = useState([]);
  const [currentEditingJobIndex, setCurrentEditingJobIndex] = useState(null);
  const [jobToEdit, setJobToEdit] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState(null);
  
  // Initialize with empty jobs list
  const [jobs, setJobs] = useState([]);
  
  const [expandedIndex, setExpandedIndex] = useState(null);

  // Project selection states
  const [isSoftwareMindSelected, setIsSoftwareMindSelected] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  // Update local state when context data changes, but only on initial load
  useEffect(() => {
    if (cvData.employmentHistory?.length > 0 && isDataReady) {
      setJobs(cvData.employmentHistory);
    }
  }, [cvData.employmentHistory, isDataReady]);
  
  // Custom handler to update both local state and context
  const handleJobsChange = (newJobs) => {
    // Update the order property for each job
    const orderedJobs = newJobs.map((job, index) => ({
      ...job,
      order: index + 1
    }));
    
    setJobs(orderedJobs);
    updateEmploymentHistory(orderedJobs);
  };

  // Function to create a new empty job
  const createNewJob = () => ({
    id: null,
    jobTitle: {
      position: null,
      grade: null,
      roles: [],
      customJobTitle: '',
      useCustomJobTitle: false
    },
    employer: '',
    city: '',
    country: '',
    startDate: null,
    endDate: null,
    currentJob: false,
    description: '',
    responsibilities: '',
    skills: [],
    order: jobs.length + 1,
    project: null,
    customProject: '',
    useCustomProject: false
  });

  // Function to handle opening the roles modal - store both job and index
  const handleOpenRolesModal = (job, jobIndex) => {
    // Store the job directly in state to avoid index issues
    setJobToEdit(job);
    
    // Set the position from jobTitle.position
    setSelectedPositionForRoles(job.jobTitle?.position || null);
    
    // Set the grade from jobTitle.grade
    setSelectedGrade({
      id: job.jobTitle?.grade?.id || '',
      label: job.jobTitle?.grade?.name || ''
    });
    
    // Extract role IDs from jobTitle.roles
    const functionalRoleIds = job.jobTitle?.roles
      ?.filter(role => role.type === ROLE_TYPES.FUNCTIONAL)
      .map(role => role.id) || [];
      
    const technicalRoleIds = job.jobTitle?.roles
      ?.filter(role => role.type === ROLE_TYPES.TECHNICAL)
      .map(role => role.id) || [];
      
    const organizationalRoleIds = job.jobTitle?.roles
      ?.filter(role => role.type === ROLE_TYPES.ORGANIZATIONAL)
      .map(role => role.id) || [];
    
    setSelectedFunctionalRoles(functionalRoleIds);
    setSelectedTechnicalRoles(technicalRoleIds);
    setSelectedOrganizationalRoles(organizationalRoleIds);
    setCurrentEditingJobIndex(jobIndex);
    setShowRolesModal(true);
  };

  // Function to handle roles update
  const handleRolesUpdate = () => {
    // Clone jobs to safely modify
    const updatedJobs = [...jobs];
    
    // If we have a direct reference to the job, use that
    if (jobToEdit) {
      // Create updated version of the job
      const updatedJob = {
        ...jobToEdit,
        jobTitle: {
          position: selectedPositionForRoles?.id ? {
            id: selectedPositionForRoles.id,
            name: selectedPositionForRoles.label || ''
          } : null,
          grade: selectedGrade?.id ? {
            id: selectedGrade.id,
            name: selectedGrade.label || ''
          } : null,
          roles: [
            // Map functional roles
            ...selectedFunctionalRoles
              .filter(roleId => selectedPositionForRoles?.roles?.some(r => r.id === roleId && r.type === ROLE_TYPES.FUNCTIONAL))
              .map(roleId => {
                const role = selectedPositionForRoles?.roles?.find(r => r.id === roleId);
                return {
                  id: roleId,
                  name: role?.label || '',
                  type: ROLE_TYPES.FUNCTIONAL
                }
              }),
            // Map technical roles
            ...selectedTechnicalRoles
              .filter(roleId => selectedPositionForRoles?.roles?.some(r => r.id === roleId && r.type === ROLE_TYPES.TECHNICAL))
              .map(roleId => {
                const role = selectedPositionForRoles?.roles?.find(r => r.id === roleId);
                return {
                  id: roleId,
                  name: role?.label || '',
                  type: ROLE_TYPES.TECHNICAL
                }
              }),
            // Include organizational roles
            ...selectedOrganizationalRoles
              .filter(roleId => selectedPositionForRoles?.roles?.some(r => r.id === roleId && r.type === ROLE_TYPES.ORGANIZATIONAL))
              .map(roleId => {
                const role = selectedPositionForRoles?.roles?.find(r => r.id === roleId);
                return {
                  id: roleId,
                  name: role?.label || '',
                  type: ROLE_TYPES.ORGANIZATIONAL
                }
              })
          ]
        }
      };
      
      // Now find the job in the array and update it
      const indexToUpdate = updatedJobs.findIndex(job => job.id === updatedJob.id);
      if (indexToUpdate !== -1) {
        updatedJobs[indexToUpdate] = updatedJob;
      } else if (currentEditingJobIndex !== null && currentEditingJobIndex !== undefined) {
        // Fallback to using the stored index
        updatedJobs[currentEditingJobIndex] = updatedJob;
      } else {
        console.error("Could not update job - no valid job or index found");
        return;
      }
      
    } else {
      console.error("No job to edit found");
      return;
    }
    
    // Update the jobs state
    handleJobsChange(updatedJobs);
    
    // Reset modal state
    setShowRolesModal(false);
    setCurrentEditingJobIndex(null);
    setJobToEdit(null);
    setSelectedPositionForRoles(null);
    setSelectedFunctionalRoles([]);
    setSelectedTechnicalRoles([]);
    setSelectedOrganizationalRoles([]);
    setSelectedGrade(null);
  };

  // Function to update the skills for a job
  const handleSkillsChange = (jobIndex, newSkills) => {

    // Handle case where parameters might be swapped or missing
    // If jobIndex is an array and newSkills is undefined, then we're getting skills as first param
    if (Array.isArray(jobIndex) && newSkills === undefined) {
      // In this case, jobIndex actually contains the skills array
      newSkills = jobIndex;
      
      // Try to get index from expandedIndex since we don't have a proper jobIndex
      jobIndex = expandedIndex;
    }
    
    // If we still don't have a valid index, we can't proceed
    if (jobIndex === null || jobIndex === undefined || typeof jobIndex !== 'number') {
      console.error('Cannot update skills: valid jobIndex is required');
      return;
    }
    // Transform newSkills to match the expected format in CVContext
    const transformedSkills = newSkills.map((skill, index) => ({
      id: skill.id,
      name: skill.name || '',
      belongsToRadar: skill.belongsToRadar || false,
      order: index + 1
    }));
    
    const updatedJobs = [...jobs];
    updatedJobs[jobIndex] = {
      ...updatedJobs[jobIndex],
      skills: transformedSkills
    };
    handleJobsChange(updatedJobs);
  };

  // Function to handle Software Mind selection
  const handleSoftwareMindSelect = async (job, onChange) => {
    setIsSoftwareMindSelected(true);
    
    // Update the job with Software Mind as employer
    onChange({
      ...job,
      employer: ORGANIZATION_NAME
    });
  };

  // Function to exit Software Mind selection mode
  const handleExitSoftwareMindMode = (job, onChange) => {
    setIsSoftwareMindSelected(false);
    setSelectedProject(null);
    
    // Update the job
    onChange({
      ...job,
      employer: '',
      project: null
    });
  };

  // Function to handle project selection
  const handleProjectSelect = (job, onChange, project) => {
    // Store the selected project
    setSelectedProject(project);

    // Update the job with the project
    onChange({
      ...job,
      project: project
    });
  };

  // Custom project handlers
  const handleToggleCustomProject = (job, onChange) => {
    const currentUseCustom = job.useCustomProject || false;
    const newUseCustom = !currentUseCustom;

    onChange({
      ...job,
      useCustomProject: newUseCustom
    });
  };

  const handleCustomProjectChange = (job, onChange, value) => {
    onChange({
      ...job,
      customProject: value
    });
  };

  // Custom classification handlers for jobs
  const handleToggleCustomJobTitle = (job, onChange) => {
    const currentUseCustom = job.jobTitle?.useCustomJobTitle || false;
    const newUseCustom = !currentUseCustom;

    onChange({
      ...job,
      jobTitle: {
        ...job.jobTitle,
        // Keep the custom text even when toggling off - just change the flag
        useCustomJobTitle: newUseCustom
      }
    });
  };

  const handleCustomJobTitleChange = (job, onChange, value) => {
    onChange({
      ...job,
      jobTitle: {
        ...job.jobTitle,
        customJobTitle: value
      }
    });
  };

  // Function to render the summary of a job (collapsed view)
  const renderJobSummary = (job) => {
    const isCustom = job.jobTitle?.useCustomJobTitle || false;

    return (
      <>
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          {isCustom ? (job.jobTitle?.customJobTitle || '(Custom classification)') : (job.jobTitle?.position?.name || '(No position specified)')} {job.employer ? `at ${job.employer}` : ''}
        </Typography>
        {(job.project || job.useCustomProject) && (
          <Typography variant="body2" color="text.secondary">
            Assigned to {job.useCustomProject ? (job.customProject || '(Custom project)') : (job.project?.name || '')} project
          </Typography>
        )}
        {!isCustom && job.jobTitle?.grade && (
          <Typography variant="body2" color="text.secondary">
            {job.jobTitle.grade.name}
          </Typography>
        )}
        {!isCustom && job.jobTitle?.roles && job.jobTitle.roles.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            {job.jobTitle.roles.map(role => role.name).join(', ')}
          </Typography>
        )}
        {(job.city || job.country) && (
          <Typography variant="body2" color="text.secondary">
            {[job.city, job.country].filter(Boolean).join(', ')}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          {formatDateRange(job.startDate, job.endDate, job.currentJob)}
        </Typography>
        {job.skills && job.skills.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            <strong>Tech stack:</strong> {job.skills.map(skill => skill.name).join(', ')}
          </Typography>
        )}
      </>
    );
  };

  // Function to render the edit form for a job (expanded view)
  const renderJobEditForm = (job, onChange, index) => {
    // Reset Software Mind selection state when a new job is expanded
    useEffect(() => {
      const isSM = job.employer === ORGANIZATION_NAME;
      setIsSoftwareMindSelected(isSM);
      
      // Set the selected project
      setSelectedProject(job.project || null);
    }, [job]);

    return (
      <Grid 
        container 
        spacing={2}
        sx={{
          width: '100%',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, 1fr)'
          },
          gap: '16px',
          '& .MuiGrid-item': {
            padding: '0',
            width: '100%'
          }
        }}
      >
        <Grid item sx={{ gridColumn: { md: '1 / span 2' } }}>
          <PositionGradeRolesBox
            position={job.jobTitle?.position}
            grade={job.jobTitle?.grade}
            roles={job.jobTitle?.roles}
            onSelectClick={() => handleOpenRolesModal(job, index)}
            useCustomClassification={job.jobTitle?.useCustomJobTitle || false}
            onToggleCustomClassification={() => handleToggleCustomJobTitle(job, onChange)}
            customClassification={job.jobTitle?.customJobTitle || ''}
            onCustomClassificationChange={(value) => handleCustomJobTitleChange(job, onChange, value)}
            readonly={readonly}
          />
        </Grid>
        
        <Grid item sx={{ gridColumn: { md: '1 / span 2' } }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Employer <span style={{ color: 'red' }}>*</span>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                fullWidth
                variant="outlined"
                value={job.employer || ''}
                onChange={(e) => onChange({ ...job, employer: e.target.value })}
                placeholder="Enter company name"
                size="small"
                autoComplete="new-password"
                name={`cv_employer_${Math.random().toString(36).substring(2, 9)}`}
                required
                disabled={isSoftwareMindSelected || readonly}
              />
              {!isSoftwareMindSelected ? (
                <Tooltip title={`Set as ${ORGANIZATION_NAME}`}>
                  <IconButton 
                    color="primary"
                    onClick={() => handleSoftwareMindSelect(job, onChange)}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 0.5,
                      borderRadius: 1,
                      '&:hover': {
                        borderRadius: 1
                      }
                    }}
                    disabled={readonly}
                  >
                    <WorkIcon />
                    <Typography variant="caption">{ORGANIZATION_NAME}</Typography>
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title={`Exit ${ORGANIZATION_NAME} mode`}>
                  <IconButton 
                    color="error"
                    onClick={() => handleExitSoftwareMindMode(job, onChange)}
                    disabled={readonly}
                  >
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            
            {isSoftwareMindSelected && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Project
                  </Typography>
                  {!readonly && (
                    <Link
                      component="button"
                      type="button"
                      variant="caption"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleToggleCustomProject(job, onChange);
                      }}
                      sx={{
                        cursor: 'pointer',
                        textDecoration: 'none',
                        border: 'none',
                        background: 'none',
                        padding: 0,
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      {job.useCustomProject ? 'Use Project Search' : 'Use Custom Project'}
                    </Link>
                  )}
                </Box>
                {job.useCustomProject ? (
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={job.customProject || ''}
                    onChange={(e) => handleCustomProjectChange(job, onChange, e.target.value)}
                    placeholder="Enter custom project name"
                    size="small"
                    disabled={readonly}
                    required={true}
                  />
                ) : (
                  <ProjectSearch
                    value={selectedProject}
                    onChange={(project) => handleProjectSelect(job, onChange, project)}
                    employeeId={cvData?.employee?.id}
                    disabled={readonly}
                    required={true}
                    width={400}
                    sx={{ width: '100%' }}
                    projectPurpose={PROJECT_PURPOSE_TYPES.DELIVERY_PROJECT}
                  />
                )}
              </Box>
            )}
          </Box>
        </Grid>
        
        <Grid item>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              City
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              value={job.city || ''}
              onChange={(e) => onChange({ ...job, city: e.target.value })}
              placeholder="Enter city"
              size="small"
              autoComplete="new-password"
              name={`cv_job_city_${Math.random().toString(36).substring(2, 9)}`}
              disabled={readonly}
            />
          </Box>
        </Grid>
        
        <Grid item>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Country
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              value={job.country || ''}
              onChange={(e) => onChange({ ...job, country: e.target.value })}
              placeholder="Enter country"
              size="small"
              autoComplete="new-password"
              name={`cv_job_country_${Math.random().toString(36).substring(2, 9)}`}
              disabled={readonly}
            />
          </Box>
        </Grid>
        
        <Grid item>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Start Date <span style={{ color: 'red' }}>*</span>
            </Typography>
            <DatePicker
              views={['year', 'month']}
              value={toPickerValue(job.startDate)}
              onChange={(date) => onChange({ ...job, startDate: fromPickerValue(date) })}
              disabled={readonly}
              slotProps={{ 
                textField: { 
                  fullWidth: true, 
                  size: "small", 
                  placeholder: "MM / YYYY",
                  required: true
                } 
              }}
            />
          </Box>
        </Grid>
        
        <Grid item>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              End Date
            </Typography>
            <DatePicker
              views={['year', 'month']}
              value={toPickerValue(job.endDate)}
              onChange={(date) => onChange({ ...job, endDate: fromPickerValue(date) })}
              disabled={job.currentJob || readonly}
              slotProps={{ 
                textField: { 
                  fullWidth: true, 
                  size: "small", 
                  placeholder: "MM / YYYY" 
                } 
              }}
            />
            <FormControlLabel
              control={
                <Checkbox 
                  checked={job.currentJob || false} 
                  onChange={(e) => {
                    const updatedJob = { 
                      ...job, 
                      currentJob: e.target.checked,
                      endDate: e.target.checked ? null : job.endDate 
                    };
                    onChange(updatedJob);
                  }}
                  size="small"
                  disabled={readonly}
                />
              }
              label="I currently work here"
              sx={{ mt: 1 }}
            />
          </Box>
        </Grid>
        
        <Grid item sx={{ gridColumn: { md: '1 / span 2' } }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Description
            </Typography>
            <RichTextEditor
              initialContent={job.description || ''}
              onChange={(content) => onChange({ ...job, description: content })}
              description="Add a brief description of your role and the company"
              readonly={readonly}
            />
          </Box>
        </Grid>
        
        <Grid item sx={{ gridColumn: { md: '1 / span 2' } }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Responsibilities
            </Typography>
            <RichTextEditor
              initialContent={job.responsibilities || ''}
              onChange={(content) => onChange({ ...job, responsibilities: content })}
              description="List your key responsibilities and achievements"
              readonly={readonly}
            />
          </Box>
        </Grid>

        <Grid item sx={{ gridColumn: { md: '1 / span 2' } }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem', fontWeight: 500 }}>
              Technology Stack
            </Typography>
            <SkillsStandalone
              category={null}
              title="Technology Stack"
              description="Add the technology stack you used in this job."
              cardVariant="none"
              hideLevels={true}
              globalSearch={true}
              skills={job.skills || []}
              onSkillsChange={(newSkills) => handleSkillsChange(expandedIndex, newSkills)}
              readonly={readonly}
            />
          </Box>
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <EditableItemList
        items={jobs}
        setItems={handleJobsChange}
        expandedIndex={expandedIndex}
        setExpandedIndex={setExpandedIndex}
        createNewItem={createNewJob}
        renderSummary={renderJobSummary}
        renderEditForm={renderJobEditForm}
        addButtonText={jobs.length > 0 ? "Add one more job" : "Add a job"}
        emptyState={{
          title: "You haven't added any jobs yet.",
          description: "Add any relevant job you've had."
        }}
        itemName="job"
        readonly={readonly}
      />

      {/* Roles selection modal */}
      <Modal
        isOpen={showRolesModal}
        onClose={() => {
          setShowRolesModal(false);
          setCurrentEditingJobIndex(null);
          setJobToEdit(null);
          setSelectedPositionForRoles(null);
          setSelectedFunctionalRoles([]);
          setSelectedTechnicalRoles([]);
          setSelectedOrganizationalRoles([]);
          setSelectedGrade(null);
        }}
        title="Select Grade, Position & Roles"
        onSubmit={handleRolesUpdate}
        submitText="Update Grade, Position & Roles"
        width="1200px"
        submitButtonProps={{
          sx: { backgroundColor: '#2563EB', color: '#FFFFFF', '&:hover': { backgroundColor: '#1E40AF' } }
        }}
      >
        <RolesSelection
          selectedPositionForRoles={selectedPositionForRoles}
          setSelectedPositionForRoles={setSelectedPositionForRoles}
          selectedFunctionalRoles={selectedFunctionalRoles}
          setSelectedFunctionalRoles={setSelectedFunctionalRoles}
          selectedTechnicalRoles={selectedTechnicalRoles}
          setSelectedTechnicalRoles={setSelectedTechnicalRoles}
          selectedOrganizationalRoles={selectedOrganizationalRoles}
          setSelectedOrganizationalRoles={setSelectedOrganizationalRoles}
          initialPosition={jobToEdit?.jobTitle?.position}
          hidePositionInfo={true}
          allowedOrgUnitTypes={[ORG_UNIT_TYPES.DELIVERY]}
          showGrades={true}
          selectedGrade={selectedGrade}
          setSelectedGrade={(grade) => {
            setSelectedGrade({
              id: grade.id,
              label: grade.label
            });
          }}
        />
      </Modal>
    </>
  );
};

export default EmploymentHistory; 